// ============================================================================
// CELL SELECTION TYPES (Sprint 2.8)
// ============================================================================

/**
 * Represents a single cell position in the table
 */
export interface CellPosition {
  rowId: string;      // The unique identifier for the row (account.id or subtotal ID)
  columnId: string;   // The column identifier (e.g., 'bookBalance', 'accountName')
}

/**
 * Represents a rectangular selection range
 */
export interface SelectionRange {
  startRow: number;   // Starting row index in visible rows
  endRow: number;     // Ending row index in visible rows
  startCol: number;   // Starting column index
  endCol: number;     // Ending column index
}

/**
 * Cell selection state
 */
export interface CellSelectionState {
  // All currently selected cells (for display and copy)
  selectedCells: Set<string>;  // Format: "rowId:columnId"
  
  // Anchor cell for shift+click range selection
  anchorCell: CellPosition | null;
  
  // Last clicked cell (for single click operations)
  lastClickedCell: CellPosition | null;
  
  // Whether we're currently in a drag selection
  isDragging: boolean;
}

/**
 * Parsed cell key for easier manipulation
 */
export function parseCellKey(key: string): CellPosition | null {
  const parts = key.split(':');
  if (parts.length !== 2) return null;
  return {
    rowId: parts[0],
    columnId: parts[1]
  };
}

/**
 * Create a cell key from position
 */
export function createCellKey(rowId: string, columnId: string): string {
  return `${rowId}:${columnId}`;
}

/**
 * Default cell selection state
 */
export const DEFAULT_CELL_SELECTION_STATE: CellSelectionState = {
  selectedCells: new Set(),
  anchorCell: null,
  lastClickedCell: null,
  isDragging: false
};

/**
 * Column definitions for selection and copy operations
 * Ordered as they appear in the table
 */
export const SELECTABLE_COLUMNS = [
  { id: 'accountNumber', label: 'Account #', type: 'text' },
  { id: 'accountName', label: 'Account Name', type: 'text' },
  { id: 'bookBalance', label: 'Book Balance', type: 'number' },
  { id: 'jeNumber', label: 'JE #', type: 'text' },
  { id: 'rje', label: 'RJE', type: 'number' },
  { id: 'aje', label: 'AJE', type: 'number' },
  { id: 'adjBook', label: 'Adj Book', type: 'number' },
  { id: 'mAdjNumber', label: 'M-Adj #', type: 'text' },
  { id: 'mAdj', label: 'M-Adj', type: 'number' },
  { id: 'ftax', label: 'Ftax', type: 'number' },
  { id: 'grouping', label: 'Grouping', type: 'text' },
  { id: 'subGrouping', label: 'SubGrouping', type: 'text' },
  { id: 'mAdjRef', label: 'M-Adj Ref', type: 'text' },
  { id: 'pyAdjBook', label: 'PY Adj Book', type: 'number' },
  { id: 'pyMAdj', label: 'PY M-Adj', type: 'number' },
  { id: 'pyFtax', label: 'PY Ftax', type: 'number' },
] as const;

export type SelectableColumnId = typeof SELECTABLE_COLUMNS[number]['id'];

/**
 * Get column index by id
 */
export function getColumnIndex(columnId: string): number {
  return SELECTABLE_COLUMNS.findIndex(col => col.id === columnId);
}

/**
 * Get column info by id
 */
export function getColumnInfo(columnId: string) {
  return SELECTABLE_COLUMNS.find(col => col.id === columnId);
}

/**
 * Summary statistics for selected cells
 */
export interface SelectionSummary {
  count: number;        // Number of selected cells with numeric values
  sum: number;          // Sum of selected numeric values (in cents)
  hasSelection: boolean;
}
